import { useState } from "react";
import { History, Loader2, RotateCcw, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api, errMsg } from "@/lib/api";
import { SOAP_KEYS } from "@/lib/soap";

const when = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function VersionPreview({ v, onBack, onRestore, restoring }) {
  const c = v.content;
  return (
    <div className="space-y-4" data-testid="version-preview">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-primary flex items-center gap-1" data-testid="version-back-button">
        <ArrowLeft className="h-3.5 w-3.5" />All versions
      </button>
      <div>
        <div className="font-semibold">{when(v.saved_at)}</div>
        <div className="text-xs text-slate-500">{v.label}{c.client_name ? ` · ${c.client_name}` : ""}{c.status === "final" ? " · Final" : " · Draft"}</div>
      </div>
      <Button className="w-full rounded-full" onClick={onRestore} disabled={restoring} data-testid="version-restore-button">
        {restoring ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}Restore this version
      </Button>
      {c.session_details && (
        <div className="rounded-lg border bg-secondary/40 p-3">
          <div className="text-xs font-semibold text-slate-500 mb-1">Session notes</div>
          <p className="text-sm whitespace-pre-wrap">{c.session_details}</p>
        </div>
      )}
      {SOAP_KEYS.map((s) => (
        <div key={s.key} className="rounded-lg border p-3">
          <div className="text-xs font-semibold text-primary mb-1">{s.label}</div>
          <p className="text-sm whitespace-pre-wrap text-slate-700">{c[s.key] || <span className="text-slate-400">Empty</span>}</p>
        </div>
      ))}
    </div>
  );
}

export function VersionHistory({ noteId, disabled, flush, onRestored }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState("");

  const openSheet = async () => {
    setOpen(true);
    setView(null);
    setList(null);
    await flush();
    api.get(`/notes/${noteId}/versions`).then((r) => setList(r.data)).catch((e) => { toast.error(errMsg(e)); setList([]); });
  };

  const show = async (id) => {
    setBusy(id);
    try { setView((await api.get(`/notes/${noteId}/versions/${id}`)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const restore = async () => {
    setBusy("restore");
    try {
      await flush();
      const { data } = await api.post(`/notes/${noteId}/versions/${view.id}/restore`);
      onRestored(data);
      toast.success(`Restored the version from ${when(view.saved_at)}. Your previous text was kept in history.`);
      setOpen(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <>
      <Button variant="outline" className="rounded-full" onClick={openSheet} disabled={disabled} data-testid="version-history-button">
        <History className="h-4 w-4 mr-2" />History
      </Button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="version-history-sheet">
          <SheetHeader className="mb-5">
            <SheetTitle>Version history</SheetTitle>
            <SheetDescription>Earlier versions are kept automatically — before AI drafts, every 10 minutes of editing and each time you press Save.</SheetDescription>
          </SheetHeader>
          {view ? (
            <VersionPreview v={view} onBack={() => setView(null)} onRestore={restore} restoring={busy === "restore"} />
          ) : list === null ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : list.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="version-history-empty">No earlier versions yet. They'll appear here as you edit this note.</p>
          ) : (
            <div className="space-y-2" data-testid="version-history-list">
              {list.map((v) => (
                <button key={v.id} onClick={() => show(v.id)} disabled={!!busy}
                  className="w-full text-left rounded-lg border p-3 hover:border-primary/40 hover:bg-secondary/40 transition-colors" data-testid={`version-item-${v.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold">{when(v.saved_at)}</span>
                    {busy === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> : <span className="text-[11px] text-slate-500">{v.label}</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{v.preview || "No note text yet"}</p>
                </button>
              ))}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
